import type { CTWRequestContext } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getZusApiBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_ACCESSIBLE_BUILDERS } from '@ctw/shared/utils/query-keys';
import { isEmpty } from 'lodash-es';

export interface Builder {
  id: string;
  type: 'auth/builders';
  attributes: {
    name: string;
    namespace?: string;
    canImpersonate?: boolean;
    billingPackage?: string;
    createdAt?: number;
    updatedAt?: number;
  };
}

interface ListBuildersResponse {
  data: Array<{
    id: string;
    type: 'auth/builders';
    attributes: Partial<Builder['attributes']>;
  }>;
  links?: { next?: string; prev?: string; self: string };
}

// Returns every builder the current user has access to: their own builder, any
// builders they reach via cross-builder grants, and (for Zus-staff users) the
// builders enumerated by their super-org. Use this anywhere we need to decide
// whether a request scoped to a specific builder is safe to make.
//
// On any failure (the auth service may not be reachable from embedded contexts,
// or the user may not have the scope) the hook falls back to a single-entry
// list containing the request-context builder so consumers always see a usable
// result.
export const useAccessibleBuilders = () =>
  useCtwQuery<Array<Builder>>({
    queryId: QUERY_KEY_ACCESSIBLE_BUILDERS,
    // The list of builders a user has access to changes infrequently and is
    // expensive to keep refetching; let it live for a while.
    staleTime: 1000 * 60 * 20,
    gcTime: 1000 * 60 * 20,
    queryFn: async ({ requestContext, ctwFetch }) => {
      const params = new URLSearchParams();
      params.set('sort', 'name');
      params.set('page[count]', '500');
      params.set('isSuperOrg', 'false');
      let nextPageUrl: string | undefined =
        `${getZusApiBaseUrl(requestContext.env)}/auth/builders?${params}`;
      const builders: Array<Builder> = [];

      while (nextPageUrl) {
        const response: { ok: boolean; data: ListBuildersResponse } = await ctwFetch(nextPageUrl, {
          headers: {
            Authorization: `Bearer ${requestContext.authToken}`,
            'Content-Type': 'application/vnd.api+json',
          },
        });

        if (!(response.ok && Array.isArray(response.data.data))) {
          return ownBuilderFallback(requestContext);
        }

        for (const builder of response.data.data) {
          builders.push({
            id: builder.id,
            type: 'auth/builders',
            attributes: {
              ...builder.attributes,
              name: isEmpty(builder.attributes.name)
                ? 'Unnamed Builder'
                : (builder.attributes.name as string),
            },
          });
        }
        nextPageUrl = response.data.links?.next;
      }

      return builders.length > 0 ? builders : ownBuilderFallback(requestContext);
    },
  });

const ownBuilderFallback = (requestContext: CTWRequestContext): Array<Builder> => [
  {
    id: requestContext.builderId,
    type: 'auth/builders',
    attributes: {
      name: requestContext.authTokenState.builderName || 'Unnamed Builder',
    },
  },
];
